// Auth state machine (03-05 §2, CONTRACT). A framework-free typed reducer that
// models sign-in STATES and EVENTS — never wire shapes. The backend's login
// response is `unknown` in the spec and MFA/SSO shapes are still pending (Q-04),
// so this contract is authored FE-side; MSW fixtures are provisional until the
// R-11 walkthrough records real traces. See [[jira-progress-tracking]].

/** MFA delivery channels the backend may challenge with (03-05 §2: TOTP/Email/SMS). */
export type MfaMethod = 'totp' | 'email' | 'sms';

/** The signed-in principal, as `/Auth/me` reports it. */
export interface AuthUser {
  userId: string;
  institutionId?: string;
  /**
   * The institution's display name, as `/Auth/me` reports it (HS-1442). Carried so a screen
   * that shows where the user belongs — the portal's profile — does not need a second read.
   */
  institutionName?: string;
  accountId?: string;
  email?: string;
  login?: string;
  firstName?: string;
  lastName?: string;
}

/**
 * The live session.
 *
 * Effective permissions are the whole authorization input. HS-1387 removed `roles` and `scopes`,
 * which were always empty: `CurrentUserInfo` exposed `permissions` only, and a role belongs to an
 * *assignment* rather than to an identity since ADR-0001. HS-980 brings `roles` back for one
 * purpose only — choosing which workspace an app shows (HS-1617 adds the caller's role names to
 * `/Auth/me`) — and never for authorization; `scopes` stays gone.
 */
export interface Session {
  user: AuthUser;
  /** Effective permission keys from `/Auth/me`. The whole authorization input. */
  permissions?: string[];
  /**
   * The role names `/Auth/me` reports for the caller (HS-1617), when it reports them.
   *
   * **Shell selection only, never authorization** (HS-980): admin shows the Installer workspace
   * to a caller whose only admin-panel role is Installer. Every access decision still reads
   * `permissions`. Absent on a backend that predates HS-1617; a mapper copies it as `[]` then.
   */
  roles?: string[];
  /**
   * The backend session id `/Auth/me` reports, when it reports one (HS-1387).
   *
   * It is carried for one reason: it is the only thing that distinguishes "this account has no
   * grants" from "the server declined to tell us what they are". `GetCurrentUserAsync` fills
   * `Permissions` only when `!sessionId.HasValue || HasScopeAccessAsync(...)`, and the DTO
   * declares `List<string> Permissions = new()`, so a withheld list arrives as `[]` and never as
   * `null`. With no `sessionId` the list is always filled, so an empty one genuinely means none;
   * with a `sessionId` present, an empty one may mean either. See `permissionsReported`.
   */
  sessionId?: string;
  /**
   * The account still holds a temporary password (HS-1292).
   *
   * It is a property of the SESSION, not of the user: the same account is on a temporary
   * password only until the change lands. `/Auth/me` reports it, so a restored session carries
   * the fact across a reload or a second tab and `createAuth` routes it to the change-password
   * challenge instead of into the app.
   *
   * `undefined` means the backend did not say. A deployment that predates the HS-1292 fix omits
   * the field entirely, and treating that silence as `false` would let a temporary-password
   * session into the app — so callers that have their own remembered flag must keep using it as
   * the fallback for exactly this case.
   */
  requiresPasswordChange?: boolean;
}

/** Normalized failure carried into the state it lands in; apps translate the key. */
export interface AuthError {
  messageKey: string;
  detail?: string;
  /**
   * The backend's machine-readable cause, when it distinguishes several failures behind one
   * status (HS-1405).
   *
   * `PUT /api/User/change-password` answers both "the new password breaks the policy" and "the
   * temporary password is wrong" with a 400, so `messageKey` — which is derived from the status —
   * is the same for both, and the change-password screens reported the first as the second.
   * `detail` cannot be used for this: it carries English prose the backend joins with `"; "`,
   * which a screen shipping EN and FR can neither show nor parse.
   */
  errorCode?: string;
  /**
   * The failure ENDS the flow instead of offering another attempt (HS-1225).
   *
   * A rejected challenge step normally returns to its own challenge so the user can retry — a
   * mistyped temporary password must not be a one-shot. Some failures are not retryable at all:
   * admin's password change succeeds on the wire and then finds no session, and asking for the
   * old password again would be asking for a password that no longer exists. Those set this and
   * land in `unauthenticated`, carrying the message with them.
   */
  endsFlow?: boolean;
}

/**
 * The resolution of a sign-in attempt (or MFA/password step). Produced by the
 * async operations layer and folded into the machine — this is what discriminates
 * the four exits of `authenticating`.
 */
export type AuthResult =
  | { outcome: 'authenticated'; session: Session }
  | { outcome: 'mfaRequired'; mfaToken: string; methods: MfaMethod[] }
  | { outcome: 'passwordChangeRequired'; flowToken: string }
  | { outcome: 'failed'; error: AuthError };

/**
 * A challenge the machine can return to (HS-1225).
 *
 * `authenticating` remembers the challenge it was entered from, so a rejected attempt can be
 * folded back into that challenge instead of collapsing to `unauthenticated`. Without this the
 * machine cannot tell "the password was wrong" from "the sign-in was refused", and the client's
 * `changePassword`/`verifyMfa` — both of which refuse to run outside their challenge state —
 * become no-ops after the first mistake.
 */
export type AuthChallenge =
  | { status: 'mfaRequired'; mfaToken: string; methods: MfaMethod[] }
  | { status: 'passwordChangeRequired'; flowToken: string };

export type AuthState =
  | { status: 'unauthenticated'; error?: AuthError }
  | { status: 'authenticating'; from?: AuthChallenge }
  | { status: 'mfaRequired'; mfaToken: string; methods: MfaMethod[]; error?: AuthError }
  | { status: 'passwordChangeRequired'; flowToken: string; error?: AuthError }
  | { status: 'authenticated'; session: Session };

/**
 * The eight contract events. The three attempt events (`SIGN_IN`, `MFA_VERIFY`,
 * `PASSWORD_CHANGED`) carry an OPTIONAL `result`: dispatched bare they begin the
 * attempt (→ `authenticating`); dispatched with a `result` they resolve it. This
 * keeps exactly the contract's event vocabulary without a separate resolve event.
 */
export type AuthEvent =
  | { type: 'SIGN_IN'; result?: AuthResult }
  | { type: 'MFA_VERIFY'; result?: AuthResult }
  | { type: 'MFA_RESEND' }
  | { type: 'PASSWORD_CHANGED'; result?: AuthResult }
  | { type: 'SIGN_OUT' }
  | { type: 'SESSION_EXPIRED' }
  | { type: 'SESSION_RESTORED'; session: Session }
  | { type: 'SESSION_UPDATED'; session: Session };

export const initialAuthState: AuthState = { status: 'unauthenticated' };

/** Message key stamped when a live session expires (single 401 policy, 03-05 §4). */
export const SESSION_EXPIRED_KEY = 'auth.error.sessionExpired';

/**
 * Fold a resolved attempt into a state.
 *
 * `previous` is the state the attempt was made FROM. It matters only for a failure: when the
 * attempt was a challenge step, the failure returns to that challenge carrying the error, so the
 * user gets another try. `endsFlow` opts out for the failures that cannot be retried.
 */
function applyResult(result: AuthResult, previous: AuthState): AuthState {
  switch (result.outcome) {
    case 'authenticated':
      return { status: 'authenticated', session: result.session };
    case 'mfaRequired':
      return { status: 'mfaRequired', mfaToken: result.mfaToken, methods: result.methods };
    case 'passwordChangeRequired':
      return { status: 'passwordChangeRequired', flowToken: result.flowToken };
    case 'failed': {
      const challenge = previous.status === 'authenticating' ? previous.from : undefined;
      if (challenge && !result.error.endsFlow) return { ...challenge, error: result.error };
      return { status: 'unauthenticated', error: result.error };
    }
  }
}

/**
 * Pure transition function. Events that don't apply to the current state are
 * ignored (state returned unchanged) rather than throwing — the async layer can
 * over-dispatch (e.g. a late resolve after `SIGN_OUT`) without corrupting state.
 */
export function authReducer(state: AuthState, event: AuthEvent): AuthState {
  switch (event.type) {
    case 'SIGN_IN':
      if (event.result) return applyResult(event.result, state);
      return state.status === 'unauthenticated' ? { status: 'authenticating' } : state;

    case 'MFA_VERIFY':
      if (event.result) return applyResult(event.result, state);
      return state.status === 'mfaRequired'
        ? {
            status: 'authenticating',
            from: { status: 'mfaRequired', mfaToken: state.mfaToken, methods: state.methods },
          }
        : state;

    case 'PASSWORD_CHANGED':
      if (event.result) return applyResult(event.result, state);
      return state.status === 'passwordChangeRequired'
        ? {
            status: 'authenticating',
            from: { status: 'passwordChangeRequired', flowToken: state.flowToken },
          }
        : state;

    case 'MFA_RESEND':
      // Resend is a side-effect (a new code is sent); the challenge state is unchanged.
      return state;

    case 'SIGN_OUT':
      return { status: 'unauthenticated' };

    case 'SESSION_EXPIRED':
      return { status: 'unauthenticated', error: { messageKey: SESSION_EXPIRED_KEY } };

    case 'SESSION_RESTORED':
      return state.status === 'unauthenticated'
        ? { status: 'authenticated', session: event.session }
        : state;

    // HS-1387: re-read an ALREADY authenticated session in place. It is the exact mirror of
    // `SESSION_RESTORED`, and the mirror is why it is a separate event rather than a relaxed
    // one: `SESSION_RESTORED` may only settle an unauthenticated machine, because letting a
    // late restore land on a challenge state is the hole HS-1225/HS-1292 closed. Widening it
    // to cover a live session would reopen that hole for the sake of this one.
    //
    // Outside `authenticated` this is a no-op — a refresh that resolves after a sign-out must
    // not resurrect the session it was reading.
    case 'SESSION_UPDATED':
      return state.status === 'authenticated'
        ? { status: 'authenticated', session: event.session }
        : state;
  }
}
